import { useState } from "react";
import {
  Alert,
  Autocomplete,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

import { failureText, scheduleLine } from "../lib/reports.js";

const WEEKDAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

/**
 * Emailing a report: how often, at what hour, and to whom.
 *
 * What is said here is what the kernel does. Each person on the list is sent their *own*
 * figure, counted from the records they can open, with a link back; the figure can differ
 * from the owner's and nothing in the email says so. They must already be able to open the
 * report (share it first, which is a separate act and is said here so nobody expects this
 * to share it). Hours are UTC, written as such, because a schedule that meant "nine" in
 * somebody's zone would send at a time nobody chose.
 */
export default function ScheduleDialog({ item, me, onClose, onSchedule }) {
  const existing = item?.schedule || null;
  const [cadence, setCadence] = useState(existing?.cadence || "daily");
  const [hour, setHour] = useState(existing?.hour ?? 9);
  const [weekday, setWeekday] = useState(existing?.weekday ?? 0);
  const [day, setDay] = useState(existing?.day ?? 1);
  const [people, setPeople] = useState(existing?.recipients || (me ? [me] : []));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const build = () => ({
    cadence, hour,
    ...(cadence === "weekly" ? { weekday } : {}),
    ...(cadence === "monthly" ? { day } : {}),
    recipients: people,
  });

  const go = async (schedule) => {
    setBusy(true);
    setError("");
    try {
      await onSchedule(item, schedule);
      onClose();
    } catch (e) {
      setError(failureText(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={Boolean(item)} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth aria-labelledby="schedule-title">
      <DialogTitle id="schedule-title" sx={{ overflowWrap: "anywhere" }}>Email “{item?.name}”</DialogTitle>
      <DialogContent dividers>
        <Stack sx={{ gap: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Each person is emailed their own figure for this report, counted from the records <em>they</em> can
            open, with a link back to it. They must already be able to open it, so share it with them first.
            Nobody is sent more than ten report emails in a day.
          </Typography>
          {existing ? <Typography variant="body2" role="status">Now: {scheduleLine(existing)}.</Typography> : null}
          <Stack direction="row" sx={{ gap: 1, flexWrap: "wrap" }}>
            <TextField select size="small" label="How often" value={cadence} onChange={(e) => setCadence(e.target.value)} sx={{ minWidth: 170 }}>
              <MenuItem value="daily">Every day</MenuItem>
              <MenuItem value="weekly">Every week</MenuItem>
              <MenuItem value="monthly">Every month</MenuItem>
            </TextField>
            {cadence === "weekly" ? (
              <TextField select size="small" label="On" value={weekday} onChange={(e) => setWeekday(Number(e.target.value))} sx={{ minWidth: 150 }}>
                {WEEKDAYS.map((d, i) => <MenuItem key={d} value={i}>{d}</MenuItem>)}
              </TextField>
            ) : null}
            {cadence === "monthly" ? (
              <TextField select size="small" label="On day" value={day} onChange={(e) => setDay(Number(e.target.value))} sx={{ minWidth: 110 }}>
                {Array.from({ length: 28 }, (_, i) => <MenuItem key={i + 1} value={i + 1}>{i + 1}</MenuItem>)}
              </TextField>
            ) : null}
            <TextField select size="small" label="At (UTC)" value={hour} onChange={(e) => setHour(Number(e.target.value))} sx={{ minWidth: 130 }}>
              {Array.from({ length: 24 }, (_, i) => <MenuItem key={i} value={i}>{String(i).padStart(2, "0")}:00 UTC</MenuItem>)}
            </TextField>
          </Stack>
          <Autocomplete
            multiple freeSolo options={me ? [me] : []} value={people}
            onChange={(_, v) => setPeople(v.map((x) => String(x).trim()).filter(Boolean))}
            renderValue={(values, getTagProps) => values.map((value, index) => {
              const { key, ...props } = getTagProps({ index });
              return <Chip key={key} size="small" label={value} {...props} />;
            })}
            renderInput={(params) => (
              <TextField {...params} label="Send to" size="small"
                helperText="Their sign-in names. Press Enter after each; at most 20." />
            )}
          />
          {error ? <Alert severity="warning" role="alert">{error}</Alert> : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        {existing ? <Button color="inherit" onClick={() => go(null)} disabled={busy}>Stop emailing</Button> : null}
        <Button variant="contained" onClick={() => go(build())} disabled={busy || !people.length}>
          {busy ? "Saving…" : "Save schedule"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
